import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCollectionId,
  getCollectionsCustomDomain,
  getDomainsList,
  getIsUserSubscribed
} from '../../../../modules/selectors';
import {
  deleteCollectionCustomDomain,
  fetchDomains,
  updateCollectionByPath,
  updateCollectionDomain
} from '../../../../modules/actions';
import styled from 'styled-components';
import { get } from 'lodash';
import { InputItem, SelectItem } from '../../../../components';
import theme from '../../../../assets/css/theme';
import { NavLink } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { getTranslationKey } from '../../../../modules/utils';
import { getAccessRules } from '../../../../modules/selectors/accessRules';
import { toUnicode } from '../../../../helpers/idn';

const SBK500_DOMAIN_ID = '634058b7d5a3c39e96ad6322';
const SBK500_DOMAIN_ENABLED = false;
const CUSTOM_DOMAIN_PATH = 'customDomain.path';
const PATH_SANITIZE_PATTERN = /[&/\\#,+()$~%.'":*?<>{}]/g;
const PATH_VALIDATION_PATTERN = /^[a-z0-9-_]+$/i;
const SBK500_DOMAINS: Record<string, string> = {
  production: 'galerie.scrappbook.de',
  development: 'galerie-development.scrappbook.de'
};
const SBK500_DOMAIN_FALLBACK = 'galerie-staging.scrappbook.de';
const SBK500_INFO_URL = 'https://scrappbook.de/neue-galerieansicht';

interface IDomainSelection {
  label?: string;
}

const Wrapper = styled.div`
  display: flex;
  margin-bottom: 5px;
  flex-wrap: wrap;
  & > div {
    flex: 1;
  }
`;

const InputWrapper = styled.div`
  margin-left: 5px;
  min-width: 90px;
`;

const UpgradeText = styled(NavLink)`
  color: ${theme.textColors.link};
`;

export const DomainSelection: React.FC<IDomainSelection> = ({ label }) => {
  const dispatch = useDispatch();
  const domains = useSelector(getDomainsList);
  const isSubscribed = useSelector(getIsUserSubscribed);
  const customDomain = useSelector(getCollectionsCustomDomain);
  // @ts-ignore
  const { domain, path } = customDomain;
  const { planConfig } = useSelector(getAccessRules);
  const domainID = get(domain, '_id', domain);
  const defaultPath = useSelector(getCollectionId);
  // Select path as a primitive string directly from the store to detect changes
  // (the customDomain object ref doesn't change due to lodash set mutation in the saga)
  // @ts-ignore
  const reduxPath = useSelector((state) => state.collectionNew?.customDomain?.path);
  const [localPath, setLocalPath] = useState(path);

  useEffect(() => {
    setLocalPath(reduxPath);
  }, [reduxPath]);

  useEffect(() => {
    dispatch(fetchDomains());
  }, [dispatch]);

  const changeDomainHandler = (value: string) => {
    if (!value) {
      dispatch(deleteCollectionCustomDomain());
    } else {
      let updatedPath = path || defaultPath;

      if (value === SBK500_DOMAIN_ID) {
        updatedPath = defaultPath;
      }

      dispatch(updateCollectionDomain({ domain: value, path: updatedPath }));
    }
  };

  const changePathHandler = (value: string) =>
    setLocalPath(value.replace(PATH_SANITIZE_PATTERN, ''));

  const updatePathHandler = () => {
    if (localPath === reduxPath) return;

    dispatch(updateCollectionByPath({ path: CUSTOM_DOMAIN_PATH, value: localPath }));
  };

  const options = domains.map((domainItem) => ({
    value: domainItem._id,
    text: toUnicode(domainItem.hostname)
  }));

  let domainSelectionIsDisabled = !isSubscribed && !planConfig.domains;

  const sbk500Domain =
    SBK500_DOMAINS[process.env.REACT_APP_ENVIRONMENT || ''] || SBK500_DOMAIN_FALLBACK;

  if (SBK500_DOMAIN_ENABLED) {
    options.push({ text: sbk500Domain, value: SBK500_DOMAIN_ID });

    domainSelectionIsDisabled = false;
  }

  return (
    <>
      <Wrapper>
        {SBK500_DOMAIN_ENABLED && (
          <p className="mb-0">
            <Alert color="warning">
              Wähle die Domain "{sbk500Domain}" aus, um die neue Galerieansicht zu nutzen. Weitere
              Informationen zur neuen Galerieansicht findest du hier:{' '}
              <a href={SBK500_INFO_URL} target="_blank" rel="noopener noreferrer">
                {SBK500_INFO_URL}
              </a>
            </Alert>
          </p>
        )}
        <SelectItem
          label={label || getTranslationKey('customDomain')}
          value={domainID}
          changeCallback={changeDomainHandler}
          options={options}
          defaultOptionsText={getTranslationKey('pleaseSelect')}
          disabled={domainSelectionIsDisabled}
        />
        {!!domain && (
          <InputWrapper>
            <InputItem
              value={localPath}
              id="collection-edit-domain-path"
              withTooltip={false}
              label={getTranslationKey('customDomains.domainPath')}
              tooltipText={getTranslationKey('collectionTitleTooltip')}
              placeholder={getTranslationKey('customDomains.domainPathPlaceholder')}
              changeCallback={changePathHandler}
              onBlurCallback={updatePathHandler}
              pattern={PATH_VALIDATION_PATTERN}
              disabled={domainID === SBK500_DOMAIN_ID}
            />
          </InputWrapper>
        )}
      </Wrapper>
      {domainSelectionIsDisabled && (
        <UpgradeText to="/subscription/pricing">
          {/* @ts-ignore */}
          <small>{getTranslationKey('customDomains.upgradeAccountForDomain')}</small>
        </UpgradeText>
      )}
    </>
  );
};

export default DomainSelection;
